/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **A collection operand, as the value line spells it** (`CO8`, slice `2e`, `#1369`).
 *
 * The model keeps each member of a list as its own kind — `{ type: 'collection', path, member }` —
 * because the engine has to tell *each member* from *a path that happens to hold a list* (`X56b`). The
 * **authoring** form is text: `pod.cachedBidRefs[*].bid`, with the `[*]` the ORTB body already uses for
 * *every element* (RS4c). So an operand is drawn on the ordinary value line, with the ordinary
 * type-down, and becomes a collection where the line hands it back — one boundary, both directions,
 * and nothing else in the editor has to learn a second control.
 *
 * Only a calculation's operand converts. Anywhere else a `[*]` stays the path it was typed as, resolves
 * to nothing, and `validateExpressionStructure` says why and where it does work.
 */

import { EACH_POSITION, joinAtEachPosition, splitAtEachPosition } from '@own-path';
import type {
  CalculatedExpression,
  CollectionOperand,
  ComputedOperand,
  ValueOperand,
} from '../../../types/decorator-expression';

export { EACH_POSITION };

/** How a collection operand reads on the value line — the path it is authored as. */
export function collectionAsLine(operand: CollectionOperand): CalculatedExpression {
  return { type: 'calculated', path: joinAtEachPosition(operand) };
}

/** What the line shows for an operand of any kind. */
export function operandOnLine(operand: ComputedOperand): ValueOperand {
  return operand.type === 'collection' ? collectionAsLine(operand) : operand;
}

/**
 * What the line hands back, read as an operand. A bare path holding `[*]` is a collection; a path that
 * has taken an extract or a transform is asking for one text value, which a collection is not, so it
 * stays the path it is — and the structure check names it.
 */
export function operandFromLine(next: ValueOperand): ComputedOperand {
  if (next.type !== 'calculated' || next.regex || next.transform) return next;
  const each = splitAtEachPosition(next.path.trim());
  return each ? { type: 'collection', ...each } : next;
}

/** Does this text name each member of a list? */
export function readsEachMember(path: string): boolean {
  return splitAtEachPosition(path.trim()) !== undefined;
}
